'use client';

import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { iconSizes } from '../theme/typography';

export interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}

/**
 * EmptyState Component
 *
 * Subtle empty state UI with icon, title, description, and optional CTA.
 * Designed as a background element — not attention-grabbing.
 *
 * Usage:
 * ```tsx
 * <EmptyState
 *   icon={<CasesIcon />}
 *   title="No cases yet"
 *   description="Get started by creating your first support case"
 *   action={<GlassButton variant="gradient">Create Case</GlassButton>}
 * />
 * ```
 */
export const EmptyState = ({ icon, title, description, action }: EmptyStateProps): React.ReactElement => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 3,
        px: 3,
        textAlign: 'center',
      }}
    >
      {icon && (
        <Box
          sx={{
            mb: 1,
            color: 'text.disabled',
            '& > svg': { fontSize: iconSizes.xl },
          }}
        >
          {icon}
        </Box>
      )}
      <Typography
        variant="body2"
        gutterBottom
        sx={{
          color: 'text.secondary',
        }}
      >
        {title}
      </Typography>
      {description && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.disabled',
            mb: 2,
            maxWidth: 400,
          }}
        >
          {description}
        </Typography>
      )}
      {action && <Box sx={{ mt: 1 }}>{action}</Box>}
    </Box>
  );
};

export default EmptyState;
